import { levelLabel, lt, t } from "@/i18n";
import { getTopic, numStr, type Question } from "@/math";
import { hero, lang, panel } from "@/state/store";
import { avatarDef } from "@/content/heroes";
import { curriculumSamples, summarize, verificationCode } from "./report";
import { renderPrompt } from "./katex";

/** Printable reports for teachers: what the hero answered, or what a level contains. Print → PDF. */
export function ReportView({ mode }: { mode: "history" | "curriculum" }) {
  const h = hero.value;
  const L = lang.value;
  const now = new Date();
  const stamp = now.toLocaleString(L === "pl" ? "pl-PL" : "en-GB");
  return (
    <div class="report">
      <div class="report-toolbar no-print">
        <button class="primary" onClick={() => window.print()}>
          🖨 {t("report.print")}
        </button>
        <button onClick={() => (panel.value = null)}>{t("report.close")}</button>
        <span class="note">{t("report.noteNonEditable")}</span>
      </div>
      <div class="report-page">
        <h1>MathRPG · {mode === "history" ? t("report.history.title") : t("report.curriculum.title")}</h1>
        <div class="report-meta">
          <span>
            {t("report.hero")}: <strong>{h.name}</strong> ({lt(avatarDef(h.avatar).name)})
          </span>
          <span>
            {t("report.level")}: <strong>{levelLabel(h.level)}</strong>
          </span>
          <span>
            {t("report.generated")}: {stamp}
          </span>
        </div>
        {mode === "history" ? <HistoryReport /> : <CurriculumReport />}
      </div>
    </div>
  );
}

function HistoryReport() {
  const h = hero.value;
  const L = lang.value;
  const records = h.history;
  const correct = records.filter((r) => r.correct).length;
  const accuracy = records.length ? Math.round((correct / records.length) * 100) : 0;
  const totalMs = records.reduce((a, r) => a + r.timeMs, 0);
  const summary = summarize(records);
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleString(L === "pl" ? "pl-PL" : "en-GB", { dateStyle: "short", timeStyle: "short" });
  const topicName = (id: string) => {
    try {
      return lt(getTopic(id).name);
    } catch {
      return id;
    }
  };

  if (records.length === 0) return <p class="empty">{t("report.empty")}</p>;

  return (
    <>
      <h2>{t("report.summary")}</h2>
      <table class="report-table summary">
        <tbody>
          <tr>
            <th>{t("report.answers")}</th>
            <td>{records.length}</td>
            <th>{t("report.correct")}</th>
            <td>{correct}</td>
            <th>{t("report.accuracy")}</th>
            <td>{accuracy}%</td>
            <th>{t("report.bestStreak")}</th>
            <td>{h.bestStreak}</td>
            <th>{t("report.time")}</th>
            <td>{Math.round(totalMs / 60000)} min</td>
          </tr>
        </tbody>
      </table>

      <h2>{t("report.topic")}</h2>
      <table class="report-table">
        <thead>
          <tr>
            <th>{t("report.topic")}</th>
            <th>{t("report.attempts")}</th>
            <th>{t("report.correct")}</th>
            <th>{t("report.accuracy")}</th>
            <th>{t("report.avgTime")}</th>
          </tr>
        </thead>
        <tbody>
          {summary.map((s) => (
            <tr key={s.topicId}>
              <td>{topicName(s.topicId)}</td>
              <td>{s.attempts}</td>
              <td>{s.correct}</td>
              <td>{Math.round((s.correct / s.attempts) * 100)}%</td>
              <td>{numStr(Math.round(s.avgTimeMs / 100) / 10, L)} s</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>
        {t("report.tasks")} ({records.length})
      </h2>
      <table class="report-table tasks">
        <thead>
          <tr>
            <th>#</th>
            <th>{t("report.date")}</th>
            <th>{t("report.topic")}</th>
            <th>{t("report.question")}</th>
            <th>{t("report.answer")}</th>
            <th>{t("report.given")}</th>
            <th>{t("report.result")}</th>
            <th>{t("report.time")}</th>
            <th>{t("report.context")}</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r, i) => (
            <tr key={i} class={r.correct ? "ok" : "bad"}>
              <td>{i + 1}</td>
              <td>{fmtDate(r.at)}</td>
              <td>{topicName(r.topicId)}</td>
              <td dangerouslySetInnerHTML={{ __html: renderPrompt(r.prompt) }} />
              <td>{r.answer}</td>
              <td>{r.given || "—"}</td>
              <td>{r.correct ? "✓" : "✗"}</td>
              <td>{numStr(Math.round(r.timeMs / 100) / 10, L)} s</td>
              <td>{t(r.context === "battle" ? "report.context.battle" : "report.context.training")}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p class="report-code">
        {t("report.code")}: <strong>{verificationCode(records, h.name)}</strong> · {t("report.codeInfo")}
      </p>
    </>
  );
}

function CurriculumReport() {
  const h = hero.value;
  const L = lang.value;
  const samples = curriculumSamples(h.level, 3);
  const byTopic = new Map<string, typeof samples>();
  for (const s of samples) byTopic.set(s.topicId, [...(byTopic.get(s.topicId) ?? []), s]);
  return (
    <>
      <p>{t("report.curriculum.intro")}</p>
      {[...byTopic.entries()].map(([topicId, tiers]) => (
        <section key={topicId} class="report-topic">
          <h2>{lt(getTopic(topicId).name)}</h2>
          {tiers.map((tier) => (
            <div key={tier.tier} class="report-tier">
              <h3>
                {t("report.tier")} {tier.tier} — {t(`training.tier.${tier.tier}`)}
              </h3>
              <ol>
                {tier.questions.map((q) => (
                  <li key={q.id}>
                    <span dangerouslySetInnerHTML={{ __html: renderPrompt(q.prompt[L]) }} />{" "}
                    <em>
                      ({t("report.answer")}: {answerOf(q)})
                    </em>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </section>
      ))}
    </>
  );
}

function answerOf(q: Question): string {
  const a = q.answer;
  const L = lang.value;
  switch (a.mode) {
    case "numeric":
      return numStr(a.value, L) + (a.unit ? (a.unit[L] === "°" ? "" : " ") + a.unit[L] : "");
    case "fraction":
      return `${a.num}/${a.den}`;
    case "choice4":
      return a.choices[a.correctIndex]?.[L] ?? "…";
    case "numericSet":
      return a.values.map((v) => numStr(v, L)).join("; ");
    case "truefalse":
      return a.value ? (L === "pl" ? "Prawda" : "True") : L === "pl" ? "Fałsz" : "False";
    case "text":
      return a.value;
  }
}
